import { isPromotionSquare } from '../game/path.js';
import { sameSquare, type Color, type Move, type PieceKind, type Square } from '../game/types.js';
import {
  DEFAULT_BOARD_SIZE,
  PIECE_FADE_DURATION_MS,
  PIECE_HOP_DURATION_MS,
  PIECE_HOP_HEIGHT,
  PIECE_MOVE_DURATION_MS,
  PIECE_PROMOTE_FLASH_MS,
} from '../utils/constants.js';
import { squareToCanvasCenter, type ViewerRotation } from './geometry.js';

/**
 * Plan for animating a single completed `Move` (quiet or capture chain).
 *
 * For a quiet move, we slide from `from` to `to` over `PIECE_MOVE_DURATION_MS`
 * with a small landing dip. For a capture chain, each segment (one per
 * captured piece) is a parabolic hop of `PIECE_HOP_DURATION_MS`, and the
 * captured opponent fades out over `PIECE_FADE_DURATION_MS` starting when the
 * hop that took it lifts off. If a man crosses its king row mid-chain, the
 * crown flashes in at that moment.
 */
export interface MoveAnimationPlan {
  readonly mover: Color;
  readonly fromKind: PieceKind;
  readonly toKind: PieceKind;
  readonly segments: readonly AnimationSegment[];
  readonly promotionSegment: number | null;
  readonly totalMs: number;
}

interface AnimationSegment {
  readonly from: Square;
  readonly to: Square;
  readonly captured: Square | null;
  readonly hop: boolean;
  readonly startMs: number;
  readonly durationMs: number;
}

export interface MoveAnimationFrame {
  readonly mover: { x: number; y: number; kind: PieceKind; scale: number };
  readonly captures: readonly { square: Square; alpha: number }[];
  readonly promotionFlash: number; // 0..1 — opacity of the promote glow
  readonly done: boolean;
}

export function planMoveAnimation(
  move: Move,
  mover: { color: Color; kind: PieceKind },
): MoveAnimationPlan {
  const segments: AnimationSegment[] = [];
  let cursor = 0;

  if (move.captures.length === 0) {
    segments.push({
      from: move.from,
      to: move.to,
      captured: null,
      hop: false,
      startMs: 0,
      durationMs: PIECE_MOVE_DURATION_MS,
    });
    cursor = PIECE_MOVE_DURATION_MS;
  } else {
    let from = move.from;
    for (let i = 0; i < move.captures.length; i += 1) {
      const to = move.path[i];
      const captured = move.captures[i];
      if (!to || !captured) continue;
      segments.push({
        from,
        to,
        captured,
        hop: true,
        startMs: cursor,
        durationMs: PIECE_HOP_DURATION_MS,
      });
      from = to;
      cursor += PIECE_HOP_DURATION_MS;
    }
  }

  // Settle time after the last hop so the captures finish fading.
  const fadeTail =
    move.captures.length > 0
      ? Math.max(0, PIECE_FADE_DURATION_MS - PIECE_HOP_DURATION_MS)
      : 0;
  const totalMs = cursor + fadeTail;

  // Locate promotion segment, if any: the segment whose landing square is the
  // mover's king row. Russian Shashki promotes mid-chain.
  let promotionSegment: number | null = null;
  if (mover.kind === 'man' && move.promoted) {
    for (let i = 0; i < segments.length; i += 1) {
      const seg = segments[i];
      if (!seg) continue;
      if (isPromotionSquare(seg.to, mover.color, DEFAULT_BOARD_SIZE)) {
        promotionSegment = i;
        break;
      }
    }
  }

  return {
    mover: mover.color,
    fromKind: mover.kind,
    toKind: move.promoted ? 'king' : mover.kind,
    segments,
    promotionSegment,
    totalMs,
  };
}

export function frameAt(
  plan: MoveAnimationPlan,
  elapsedMs: number,
  rotation: ViewerRotation = 0,
): MoveAnimationFrame {
  const clamped = Math.max(0, elapsedMs);
  const done = clamped >= plan.totalMs;

  // Find the active segment (or the last one if we're past the end).
  let active = plan.segments[plan.segments.length - 1];
  let activeIndex = plan.segments.length - 1;
  for (let i = 0; i < plan.segments.length; i += 1) {
    const seg = plan.segments[i];
    if (!seg) continue;
    if (clamped < seg.startMs + seg.durationMs) {
      active = seg;
      activeIndex = i;
      break;
    }
  }
  if (!active) {
    // No segments: degenerate plan. Park the mover at the move's start.
    return {
      mover: { x: 0, y: 0, kind: plan.fromKind, scale: 1 },
      captures: [],
      promotionFlash: 0,
      done: true,
    };
  }

  const segElapsed = Math.min(active.durationMs, clamped - active.startMs);
  const t = active.durationMs > 0 ? segElapsed / active.durationMs : 1;
  const eased = easeOutCubic(t);

  const fromPt = squareToCanvasCenter(active.from, rotation);
  const toPt = squareToCanvasCenter(active.to, rotation);
  const x = fromPt.x + (toPt.x - fromPt.x) * eased;
  let y = fromPt.y + (toPt.y - fromPt.y) * eased;

  // Hop arc + tiny squash on landing for quiet moves.
  let scale = 1;
  if (active.hop) {
    y -= Math.sin(Math.PI * t) * PIECE_HOP_HEIGHT;
  } else {
    // Subtle landing settle on quiet moves.
    const settle = t > 0.9 ? Math.sin((t - 0.9) / 0.1 * Math.PI) : 0;
    scale = 1 - 0.05 * settle;
  }

  // Captures fade in parallel with the hop that lifts them.
  const captures: { square: Square; alpha: number }[] = [];
  for (const seg of plan.segments) {
    if (!seg.captured) continue;
    const fadeElapsed = clamped - seg.startMs;
    if (fadeElapsed <= 0) {
      captures.push({ square: seg.captured, alpha: 1 });
      continue;
    }
    const fadeT = Math.min(1, fadeElapsed / PIECE_FADE_DURATION_MS);
    const alpha = 1 - easeOutCubic(fadeT);
    if (alpha > 0) captures.push({ square: seg.captured, alpha });
  }

  // King kind switches the moment the mover lands on the promotion segment.
  let kind: PieceKind = plan.fromKind;
  let promotionFlash = 0;
  if (plan.promotionSegment !== null) {
    const promoSeg = plan.segments[plan.promotionSegment];
    if (promoSeg) {
      const landedAt = promoSeg.startMs + promoSeg.durationMs;
      if (clamped >= landedAt) {
        kind = 'king';
        const flashT = Math.min(1, (clamped - landedAt) / PIECE_PROMOTE_FLASH_MS);
        promotionFlash = 1 - flashT;
      }
    }
  } else if (plan.toKind === 'king') {
    // No mid-chain promotion: piece may already be a king or never promotes.
    kind = activeIndex === plan.segments.length - 1 && t >= 1 ? plan.toKind : plan.fromKind;
  }
  if (done) kind = plan.toKind;

  return {
    mover: { x, y, kind, scale },
    captures,
    promotionFlash,
    done,
  };
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

/**
 * Helper that drives a plan with requestAnimationFrame. Returns a function
 * that cancels the animation. The host renders the rest of the scene each
 * tick via `onFrame`.
 */
export function runMoveAnimation(
  plan: MoveAnimationPlan,
  rotation: ViewerRotation,
  onFrame: (frame: MoveAnimationFrame) => void,
  onComplete?: () => void,
): () => void {
  const start = performance.now();
  let cancelled = false;
  let rafId = 0;

  const step = (): void => {
    if (cancelled) return;
    const frame = frameAt(plan, performance.now() - start, rotation);
    onFrame(frame);
    if (frame.done) {
      onComplete?.();
      return;
    }
    rafId = requestAnimationFrame(step);
  };

  rafId = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
  };
}

/** Where is the moving piece "currently sitting" in board terms? Useful when
 * the host wants to skip drawing the static piece at that square while the
 * animation drives it. */
export function activeOriginSquare(plan: MoveAnimationPlan): Square {
  const first = plan.segments[0];
  return first ? first.from : { row: 0, col: 0 };
}

/** Whether `square` is the source of the active move (so the static piece
 * renderer can skip it). */
export function isAnimatedSource(plan: MoveAnimationPlan, square: Square): boolean {
  return sameSquare(activeOriginSquare(plan), square);
}
